import { type ComponentRef, useEffect, useRef } from 'react';

import { useSessionStorage } from '@/hooks/useSessionStorage';
import { logChatRenderDebug } from '@/utils/chat-render-debug';

const SCROLL_POSITION_KEY = 'scroll-position';
const SCROLL_SAVE_DEBOUNCE_MS = 300;

/**
 * Finds the nearest scrollable ancestor element
 */
function findScrollableParent(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null;

  let parent = element.parentElement;
  while (parent) {
    const { overflow, overflowY } = window.getComputedStyle(parent);
    if (
      overflow === 'auto' ||
      overflow === 'scroll' ||
      overflowY === 'auto' ||
      overflowY === 'scroll'
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
}

/**
 * Props for the ScrollRestoration component
 */
export interface ScrollRestorationProps {
  /**
   * Unique identifier for this scroll position instance
   */
  id: string;
  /**
   * Skip restoring saved scroll on this mount
   */
  skipInitialRestore?: boolean;
  /**
   * Callback invoked on mount with saved scroll position (only if position exists).
   * When omitted, the component auto-scrolls the nearest scrollable ancestor.
   * @param scrollTop - The saved scroll position in pixels
   */
  onRestoreScroll?: (scrollTop: number) => void;
  /**
   * Optional callback invoked after restoration attempt completes (always fires)
   * Use for ready state signaling or post-restoration logic
   */
  onReady?: () => void;
}

/**
 * ScrollRestoration - Pure side-effects component for scroll position persistence
 *
 * Saves scroll position to sessionStorage and restores it on mount via callback.
 * Does not render any visible elements.
 *
 * @remarks
 * **How it works:**
 * - Places an invisible marker element to detect scrollable parent
 * - On mount: reads saved position from sessionStorage, calls onRestoreScroll callback
 * - On scroll: debounces (300ms) then saves position to sessionStorage
 * - Position keyed by id, persists per-tab (cleared on close)
 *
 * **Parent responsibility:**
 * - Component does NOT apply scroll itself
 * - Parent receives position via callback and applies it
 *
 * @example
 * ```tsx
 * const scrollRef = useRef<HTMLDivElement>(null);
 * const [isReady, setIsReady] = useState(false);
 *
 * return (
 *   <div ref={scrollRef} className="overflow-y-auto">
 *     {messages.map(msg => <Message key={msg.id} {...msg} />)}
 *     <ScrollRestoration
 *       id="chat-messages"
 *       onRestoreScroll={(scrollTop) => {
 *         scrollRef.current?.scrollTo({ top: scrollTop });
 *       }}
 *       onReady={() => {
 *         requestAnimationFrame(() => setIsReady(true));
 *       }}
 *     />
 *   </div>
 * );
 * ```
 */
export function ScrollRestoration({
  id,
  skipInitialRestore = false,
  onRestoreScroll,
  onReady,
}: ScrollRestorationProps) {
  const markerRef = useRef<ComponentRef<'div'>>(null);
  const [positionMap, setPositionMap] = useSessionStorage<
    Record<string, number>
  >(SCROLL_POSITION_KEY, {});
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRestoredRef = useRef(false);

  useEffect(() => {
    if (hasRestoredRef.current) return;

    if (!skipInitialRestore) {
      const saved = positionMap[id];
      logChatRenderDebug('scroll-restoration-mount', {
        id,
        saved: saved === undefined ? null : Math.round(saved),
      });
      if (saved !== undefined) {
        if (onRestoreScroll) {
          onRestoreScroll(saved);
        } else {
          findScrollableParent(markerRef.current)?.scrollTo({ top: saved });
        }
      }
    }

    hasRestoredRef.current = true;
    onReady?.();
  }, [skipInitialRestore, positionMap, id, onRestoreScroll, onReady]);

  useEffect(() => {
    const scrollableParent = findScrollableParent(markerRef.current);

    if (!scrollableParent) return;

    const handleScroll = () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

      saveTimerRef.current = setTimeout(() => {
        logChatRenderDebug('scroll-restoration-save', {
          id,
          scrollTop: Math.round(scrollableParent.scrollTop),
        });
        setPositionMap((prev) => ({
          ...prev,
          [id]: scrollableParent.scrollTop,
        }));
      }, SCROLL_SAVE_DEBOUNCE_MS);
    };

    scrollableParent.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    return () => {
      scrollableParent.removeEventListener('scroll', handleScroll);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [id, setPositionMap]);

  return <div ref={markerRef} className="size-0" />;
}
